import { ICouponProgram } from "@interfaces";
import React, { memo, useEffect } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { FormAction } from "@components";
import { PATH_CDIC_COUPON_LIST } from "@configs";
import { setLoading } from "@redux";
import {
  getApiErrorMessage,
  useCreateProgram,
  useNotify,
  useUpdateProgram,
} from "@utils";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";

interface IProps {
  coupon?: ICouponProgram;
}

const initValue: Partial<ICouponProgram> = {
  programName: "",
  noOfCoupons: 0,
  availableCoupons: 0,
};

export const CdicProgramForm = memo(({ coupon }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupProgram } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();
  const couponSchema = Yup.object().shape(YupProgram);
  const [couponForm] = Form.useForm();

  useEffect(() => {
    dispatch(setLoading(createProgram.isPending || updateProgram.isPending));
  }, [dispatch, createProgram.isPending, updateProgram.isPending]);

  const onFinish = async (values: ICouponProgram) => {
    const payload = {
      ...values,
      noOfCoupons: Number(values.noOfCoupons),
      availableCoupons: Number(values.noOfCoupons),
    };
    try {
      if (coupon && coupon._id) {
        await updateProgram.mutateAsync({ id: coupon._id, payload });
      } else {
        await createProgram.mutateAsync(payload);
      }
      success(t("message.create.success"));
      history.push(PATH_CDIC_COUPON_LIST);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await couponSchema.validateSyncAt(field, { [field]: value });
    },
  };

  return (
    <Form
      form={couponForm}
      name="basic"
      ref={formRef}
      initialValues={initValue}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.coupon_program_name")} name="programName" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.number_of_coupons")} name="noOfCoupons" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <FormAction />
    </Form>
  );
});
